'use client'

import { create } from 'zustand'

export type TaskFlowType =
  | 'create_content'
  | 'edit_product'
  | 'build_funnel'
  | 'create_offer'
  | 'edit_scene'
  | 'create_portrait'
  | 'create_flyer'
  | 'generic'

export type TaskFlowPhase =
  | 'idle'
  | 'creative_direction'
  | 'discovery'
  | 'product_input'
  | 'analyzing'
  | 'proposed_plan'
  | 'executing'
  | 'creating_character'
  | 'generating_avatar'
  | 'generating_scenes'
  | 'rendering_final_video'
  | 'result'
  | 'error'

export type TaskFlowRequest = {
  taskType: TaskFlowType
  worker?: string | null
  title: string
  subtitle?: string | null
  routeFallback?: string | null
  payload?: Record<string, any>
  phase?: TaskFlowPhase
  requiresLLM?: boolean
}

type TaskFlowState = {
  isOpen: boolean
  request: TaskFlowRequest | null
  phase: TaskFlowPhase
  result: any | null
  error: string | null
  snapshotId: number | null

  openTaskFlow: (request: TaskFlowRequest) => void
  updateTaskFlow: (patch: Partial<TaskFlowRequest>) => void
  setPhase: (phase: TaskFlowPhase) => void
  setResult: (result: any) => void
  setError: (error: string | null) => void
  setSnapshotId: (id: number | null) => void

  hydrateFromSnapshot: (payload: {
    phase: TaskFlowPhase
    request: TaskFlowRequest | null
    result: any
    snapshotId?: number | null
  }) => void

  closeTaskFlow: () => void
  resetTaskFlow: () => void
}

export const useTaskFlowStore = create<TaskFlowState>((set) => ({
  isOpen: false,
  request: null,
  phase: 'idle',
  result: null,
  error: null,
  snapshotId: null,

  openTaskFlow: (request) =>
    set({
      isOpen: true,
      request,
      phase: request.phase || 'creative_direction',
      result: null,
      error: null,
    }),

  updateTaskFlow: (patch) =>
    set((state) => ({
      request: state.request ? { ...state.request, ...patch } : null,
    })),

  setPhase: (phase) => set({ phase }),

  setResult: (result) => set({ result }),

  setSnapshotId: (snapshotId) => set({ snapshotId }),

  hydrateFromSnapshot: (payload) =>
    set({
      isOpen: true,
      phase: payload.phase,
      request: payload.request || null,
      result: payload.result || null,
      snapshotId: payload.snapshotId || null,
      error: null,
    }),

  setError: (error) => set({ error, phase: error ? 'error' : 'idle' }),

  closeTaskFlow: () =>
    set({
      isOpen: false,
    }),

  resetTaskFlow: () =>
    set({
      isOpen: false,
      request: null,
      phase: 'idle',
      result: null,
      error: null,
      snapshotId: null,
    }),
}))